import { createElement, useState } from 'react';
import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi';

import { ButtonOutlinedSmall } from '@/components/Button';
import Modal from '@/components/Modal/Modal';
import type { TransactionCategory } from '@/store/expenses-store/transactions/transactions.dto';
import { useTransactionsStore } from '@/store/expenses-store/transactions/transactions.store';
import { toast } from '@/utils/toast.ts';
import { resolveIcon } from '@pages/Expenses/helpers/iconRegistry';

import { CategoryModal } from './CategoryModal';
import { DeleteCategoryModal } from './DeleteCategoryModal';
import s from './ManageCategoriesModal.module.scss';

/** Matches the budget list: a category the service left without a colour still needs a chip. */
const DEFAULT_ICON_COLOR = '#677687';

interface ManageCategoriesModalProps {
  open: boolean;
  onClose: () => void;
  /** Runs after a create, edit or delete — the transaction rows carry category names too. */
  onChanged?: () => void | Promise<void>;
}

/**
 * The category list of the transactions page, mirroring the one behind the budget's plans
 * modal: same rows, same three actions, minus the planned amounts. The vessel's protected
 * "Other" is listed but cannot be edited or deleted — wallet empties deleted categories into it.
 */
export function ManageCategoriesModal({ open, onClose, onChanged }: ManageCategoriesModalProps) {
  const categories = useTransactionsStore((state) => state.transactionCategories);
  const removeCategory = useTransactionsStore((state) => state.deleteTransactionCategory);

  const [createOpen, setCreateOpen] = useState(false);
  const [editCategory, setEditCategory] = useState<TransactionCategory | null>(null);
  const [deleteCategory, setDeleteCategory] = useState<TransactionCategory | null>(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async (targetCategoryId: string | undefined) => {
    if (!deleteCategory) return;
    setDeleting(true);
    try {
      await removeCategory(deleteCategory.id, targetCategoryId);
      setDeleteCategory(null);
      toast.success('Category deleted');
      await onChanged?.();
    } catch {
      // The store already reported the failure; the modal stays open so it can be retried.
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose} title="Manage Categories">
        <div className={s.content}>
          <ul className={s.list}>
            {categories.map((category) => (
              <li key={category.id} className={s.row}>
                <div
                  className={s.icon}
                  style={{ background: category.color || DEFAULT_ICON_COLOR }}
                >
                  {createElement(resolveIcon(category.icon), { size: 16 })}
                </div>
                <span className={s.name}>{category.title}</span>
                {!category.is_default && (
                  <div className={s.rowActions}>
                    <button
                      type="button"
                      className={s.rowAction}
                      onClick={() => setEditCategory(category)}
                      aria-label={`Edit ${category.title}`}
                    >
                      <FiEdit2 size={14} />
                    </button>
                    <button
                      type="button"
                      className={`${s.rowAction} ${s.rowActionDanger}`}
                      onClick={() => setDeleteCategory(category)}
                      aria-label={`Delete ${category.title}`}
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                )}
              </li>
            ))}
            <li>
              <button type="button" className={s.addBtn} onClick={() => setCreateOpen(true)}>
                <FiPlus size={14} />
                Add Category
              </button>
            </li>
          </ul>

          <div className={s.actions}>
            <ButtonOutlinedSmall type="button" onClick={onClose}>
              Done
            </ButtonOutlinedSmall>
          </div>
        </div>
      </Modal>

      <CategoryModal open={createOpen} onClose={() => setCreateOpen(false)} onSuccess={onChanged} />

      <CategoryModal
        open={!!editCategory}
        onClose={() => setEditCategory(null)}
        category={editCategory}
        onSuccess={onChanged}
        isEdit
      />

      <DeleteCategoryModal
        open={!!deleteCategory}
        onClose={() => setDeleteCategory(null)}
        category={deleteCategory}
        categories={categories}
        onConfirm={confirmDelete}
        deleting={deleting}
      />
    </>
  );
}
